import { InvestPaymentMethodType, InvestPaymentMethodTypes, PaymentPageType } from '../../../types/mf-execution.types'
import { investPaymentMethodConfig, upiIcon } from '../../../utils/mf-execution/paymentPageConfig'
import { PaymentMethodsButton } from '../PaymentMethodsButton'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePaymentMethodAutoSwitchUpiIntents } from './usePaymentMethodAutoSwitch.upiIntents'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { EventNames } from '../../../types/misc.types'
import { trackEvent } from '../../../utils/appBridge'
import { buildAmountQueryParam } from '../../../utils/mf-execution/paymentAmount'
import {
  buildUpiIntentPaymentMethod,
  filterInstalledUpiAppsForPayment,
  findFirstUpiPaymentMethod,
  findNetBankingPaymentMethod,
  getDefaultInstalledUpiApp,
  getInitialSelectedBankAccount,
  getUpiIntentDisplayForPaymentMethod,
  isUpiIntentPaymentMethod,
} from '../../../utils/mf-execution/paymentMethodSelection'
import { BuyPaymentMethodsButtonUpiIntentsProps } from './buyPaymentMethodsButton.types'

export const BuyPaymentMethodsButtonUpiIntents = ({
  amount,
  isDisabled,
  handlePayNowClick,
  selectedPaymentMethod,
  handlePaymentMethodChange,
  paymentMethods,
  paymentType,
  trackingFund,
  installedUpiApps,
  bankAccountDetails,
  defaultPaymentMethodDetails,
  requestUpiIntents,
  upiIntentMaximumAllowedAmount,
}: BuyPaymentMethodsButtonUpiIntentsProps) => {
  const navigate = useNavigate()
  const { isin } = useMetaDataContext()

  const eligibleInstalledUpiApps = useMemo(
    () => filterInstalledUpiAppsForPayment(installedUpiApps),
    [installedUpiApps]
  )

  const areInstalledUpiIntentsReady = installedUpiApps !== null
  const hasInstalledUpiIntents = areInstalledUpiIntentsReady && eligibleInstalledUpiApps.length > 0
  const netBankingMethod = useMemo(() => findNetBankingPaymentMethod(paymentMethods), [paymentMethods])
  const apiUpiMethod = useMemo(() => findFirstUpiPaymentMethod(paymentMethods), [paymentMethods])

  const firstUpiIntentMethod = useMemo(() => {
    const firstApp = getDefaultInstalledUpiApp(eligibleInstalledUpiApps, defaultPaymentMethodDetails)
    if (!firstApp) return null

    const bankAccount = getInitialSelectedBankAccount(bankAccountDetails, defaultPaymentMethodDetails)
    return buildUpiIntentPaymentMethod(firstApp, paymentMethods, bankAccount, bankAccountDetails)
  }, [eligibleInstalledUpiApps, paymentMethods, bankAccountDetails, defaultPaymentMethodDetails])

  const { blockBecauseNotAvailablePaymentMethod } = usePaymentMethodAutoSwitchUpiIntents({
    amount,
    paymentMethods,
    selectedPaymentMethod,
    handlePaymentMethodChange,
    firstUpiIntentMethod,
    upiIntentMaximumAllowedAmount,
    hasInstalledUpiIntents,
    areInstalledUpiIntentsReady,
  })

  useEffect(() => {
    requestUpiIntents()
  }, [requestUpiIntents])

  useEffect(() => {
    if (!paymentMethods?.length || !areInstalledUpiIntentsReady) return

    if (firstUpiIntentMethod?.paymentMethodId) {
      const shouldSelectUpiIntent =
        !selectedPaymentMethod?.paymentMethodId ||
        selectedPaymentMethod?.type === InvestPaymentMethodTypes.UPI

      if (shouldSelectUpiIntent) {
        handlePaymentMethodChange(firstUpiIntentMethod)
      }
      return
    }

    if (
      selectedPaymentMethod?.type === InvestPaymentMethodTypes.NET_BANKING ||
      selectedPaymentMethod?.type === InvestPaymentMethodTypes.MANDATE ||
      isUpiIntentPaymentMethod(selectedPaymentMethod)
    ) {
      return
    }

    if (!hasInstalledUpiIntents && netBankingMethod) {
      const shouldSelectNetBanking =
        !selectedPaymentMethod?.paymentMethodId ||
        selectedPaymentMethod?.type === InvestPaymentMethodTypes.UPI ||
        isUpiIntentPaymentMethod(selectedPaymentMethod)

      if (shouldSelectNetBanking) {
        handlePaymentMethodChange(netBankingMethod)
      }
      return
    }

    if (selectedPaymentMethod?.paymentMethodId) return

    if (apiUpiMethod?.paymentMethodId) {
      handlePaymentMethodChange(apiUpiMethod)
      return
    }

    handlePaymentMethodChange(null)
  }, [
    paymentMethods,
    areInstalledUpiIntentsReady,
    firstUpiIntentMethod,
    hasInstalledUpiIntents,
    netBankingMethod,
    apiUpiMethod,
    selectedPaymentMethod,
    handlePaymentMethodChange,
  ])

  const upiIntentDisplay = useMemo(
    () => getUpiIntentDisplayForPaymentMethod(selectedPaymentMethod, eligibleInstalledUpiApps),
    [selectedPaymentMethod, eligibleInstalledUpiApps]
  )

  const selectedPaymentConfig = useMemo(() => {
    if (!selectedPaymentMethod) return null
    if (isUpiIntentPaymentMethod(selectedPaymentMethod)) {
      return { icon: upiIcon, label: upiIntentDisplay.appName || 'Pay with UPI' }
    }
    return investPaymentMethodConfig?.[selectedPaymentMethod?.type] ?? null
  }, [selectedPaymentMethod, upiIntentDisplay.appName])

  const middleTexts = useMemo(
    () => [
      {
        type: 'WITH_DOWN_CARAT' as const,
        text: getPaymentMethodPrimaryLabel(selectedPaymentMethod, upiIntentDisplay, selectedPaymentConfig),
      },
      {
        type: 'NORMAL' as const,
        text: selectedPaymentMethod?.displayAccNumber || '',
      },
    ],
    [selectedPaymentMethod, selectedPaymentConfig, upiIntentDisplay]
  )

  const handlePaymentMethodsClick = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: paymentType === PaymentPageType.SIP ? 'SIP payment' : 'One time Enter amount',
        eventId: 'MFE_SELECT_AMOUNT_CTA_SELECT_BANK',
        buttonLabel: 'Payment method',
        ...(trackingFund ?? {}),
      },
    })
    const paymentPageType = paymentType ?? PaymentPageType.BUY
    navigate(`/mutual-fund/${isin}/payment/${paymentPageType}/upi-intents${buildAmountQueryParam(amount)}`)
  }

  const showAddUpiOnly =
    areInstalledUpiIntentsReady && !hasInstalledUpiIntents && !apiUpiMethod && !netBankingMethod

  if (!areInstalledUpiIntentsReady) {
    return null
  }

  if (showAddUpiOnly) {
    return (
      <PaymentMethodsButton
        iconSVG={upiIcon}
        onMiddleTextsClick={handlePaymentMethodsClick}
        middleTexts={[{ type: 'WITH_DOWN_CARAT', text: 'Pay with UPI' }]}
        onCTAClick={() => handlePayNowClick('ADD_UPI')}
        ctaLabel='Proceed'
        isDisabled={isDisabled}
      />
    )
  }

  if (!selectedPaymentConfig || !selectedPaymentMethod) {
    return null
  }

  return (
    <PaymentMethodsButton
      iconSVG={upiIntentDisplay.appLogoUrl ? undefined : selectedPaymentConfig?.icon}
      iconUrl={upiIntentDisplay.appLogoUrl || undefined}
      onMiddleTextsClick={handlePaymentMethodsClick}
      middleTexts={middleTexts}
      onCTAClick={() => handlePayNowClick('PAY_NOW')}
      ctaLabel={paymentType === PaymentPageType.SIP ? 'Continue' : 'Pay now'}
      isDisabled={isDisabled || blockBecauseNotAvailablePaymentMethod}
    />
  )
}

export function getPaymentMethodPrimaryLabel(
  selectedPaymentMethod: InvestPaymentMethodType | null,
  upiIntentDisplay: { appName: string },
  selectedPaymentConfig: { label: string } | null
) {
  if (isUpiIntentPaymentMethod(selectedPaymentMethod)) {
    return upiIntentDisplay.appName || selectedPaymentConfig?.label || ''
  }

  if (selectedPaymentMethod?.type === InvestPaymentMethodTypes.UPI) {
    return selectedPaymentMethod?.upiId || selectedPaymentConfig?.label || ''
  }

  return selectedPaymentConfig?.label || ''
}
